import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { API_STRING as api } from '@/App';
import { logger } from '@/utils/logger';
import { Loader2, Search, Wand2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function DeckFormModal({
  open,
  onOpenChange,
  deckFormData,
  setDeckFormData,
  products,
  decks = [],
  selectedProductsForDeck,
  handleProductToggleForDeck,
  handleCreateDeck
}) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category).filter(Boolean))].sort(),
    [products]
  );

  // product id -> titles of other decks it already belongs to
  const inOtherDecks = useMemo(() => {
    const map = {};
    decks.forEach((d) => {
      (d.product_ids || []).forEach((pid) => {
        if (!map[pid]) map[pid] = [];
        map[pid].push(d.title);
      });
    });
    return map;
  }, [decks]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (categoryFilter && p.category !== categoryFilter) return false;
      if (q && !`${p.name} ${p.category} ${(p.tags || []).join(' ')}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [products, search, categoryFilter]);

  const selectedProducts = selectedProductsForDeck
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean);

  const suggestWithAI = async () => {
    if (!deckFormData.title.trim()) {
      toast.info('Give the deck a title first so the AI knows the theme');
      return;
    }
    setAiLoading(true);
    try {
      const res = await fetch(`${api}/admin/ai/suggest-deck-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: deckFormData.title, description: deckFormData.description }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Suggestion failed');
      const ids = (data.product_ids || []).filter((id) => products.some((p) => p.id === id));
      if (ids.length === 0) {
        toast.info('No strong matches found for that theme. Try a more specific title.');
        return;
      }
      // Replace current selection with the AI picks.
      selectedProductsForDeck.forEach((id) => {
        if (!ids.includes(id)) handleProductToggleForDeck(id);
      });
      ids.forEach((id) => {
        if (!selectedProductsForDeck.includes(id)) handleProductToggleForDeck(id);
      });
      // Apply the AI's polished copy: fill description when empty, and title
      // if the admin only typed a rough theme and the AI improved it.
      if (data.description && !deckFormData.description.trim()) {
        setDeckFormData((prev) => ({ ...prev, description: data.description }));
      }
      toast.success(`✨ ${ids.length} products picked for "${deckFormData.title}". Review and adjust.`);
    } catch (err) {
      logger.error('AI deck suggestion failed:', err);
      toast.error(err.message || 'AI suggestion failed');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto admin-area" data-testid="craft-deck-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
            Craft a Deck
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm" style={{ color: '#73766A' }}>
            Create a bundle of 2-4 products that work well together
          </p>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#20212B' }}>
              Deck Title *
            </label>
            <Input
              data-testid="deck-title-input"
              placeholder="e.g., Tech Starter Pack"
              value={deckFormData.title}
              onChange={(e) => setDeckFormData({ ...deckFormData, title: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#20212B' }}>
              Description
            </label>
            <textarea
              data-testid="deck-description-input"
              placeholder="Brief description of this deck..."
              value={deckFormData.description}
              onChange={(e) => setDeckFormData({ ...deckFormData, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-md"
              rows="2"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <label className="text-sm font-medium" style={{ color: '#20212B' }}>
                Select Products (2-4 required) *
              </label>
              <button
                type="button"
                data-testid="deck-ai-suggest-btn"
                onClick={suggestWithAI}
                disabled={aiLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-transform hover:scale-105 disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #9E6752 0%, #C97C5F 100%)' }}
              >
                {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {aiLoading ? 'Picking…' : 'Suggest for this theme'}
              </button>
            </div>

            {/* Selected preview strip */}
            {selectedProducts.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2 p-2 rounded-lg" data-testid="deck-selected-strip" style={{ background: 'rgb(254, 215, 165)' }}>
                {selectedProducts.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full bg-white text-xs font-medium text-[#20212B]">
                    <img src={p.image_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                    <span className="max-w-[140px] truncate">{p.name}</span>
                    <button
                      type="button"
                      data-testid={`deck-selected-remove-${p.id}`}
                      onClick={() => handleProductToggleForDeck(p.id)}
                      className="rounded-full p-0.5 hover:bg-gray-200"
                      aria-label={`Remove ${p.name}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Search + category filter */}
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  data-testid="deck-product-search"
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <select
                data-testid="deck-category-filter"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm max-w-[160px]"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="border rounded-lg p-4 max-h-80 overflow-y-auto" style={{ background: '#F5F5F5' }}>
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-500">No products match your search</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {filtered.map(product => {
                    const otherDecks = (inOtherDecks[product.id] || []).filter((t) => t !== deckFormData.title);
                    return (
                      <label
                        key={product.id}
                        className="flex items-center gap-3 p-2 rounded cursor-pointer transition-colors hover:bg-white dark:hover:bg-[#3A4B5C]"
                      >
                        <input
                          type="checkbox"
                          checked={selectedProductsForDeck.includes(product.id)}
                          onChange={() => handleProductToggleForDeck(product.id)}
                          className="w-4 h-4"
                        />
                        <img src={product.image_url} alt={product.name} className="w-10 h-10 object-cover rounded" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{product.name}</p>
                          <p className="text-xs text-gray-500">
                            ${product.price}
                            {otherDecks.length > 0 && (
                              <span
                                className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium align-middle"
                                style={{ background: '#FEF3C7', color: '#92400E' }}
                                title={`Already in: ${otherDecks.join(', ')}`}
                              >
                                in {otherDecks.length} deck{otherDecks.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="text-xs mt-1" style={{ color: selectedProductsForDeck.length < 2 || selectedProductsForDeck.length > 4 ? '#DC2626' : '#73766A' }}>
              {selectedProductsForDeck.length} products selected (need 2-4)
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              className="bg-white text-[#20212B] border border-gray-300 hover:bg-gray-100"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              data-testid="deck-create-btn"
              onClick={handleCreateDeck}
              style={{ background: '#2D4354', color: 'white' }}
            >
              Create Deck
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
